import "../assets/qreader.css";
import { useState } from "react";
import { axiosInstance } from "../utils/axios";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useDialogStore from "../stores/useDialogStore";
import { useForm } from "react-hook-form";

export default function MoveDevices() {
  const { closeDialog } = useDialogStore();
  const queryClient = useQueryClient();
  const [textareaValue, setTextareaValue] = useState("");

  const { register, setValue, watch, handleSubmit } = useForm({
    defaultValues: {
      blue: 0,
      green: 0,
      recycled_date: null,
      spare_parts: null,
    },
  });

  /* ---------------- IDS ---------------- */
  const uniqueIdsArray = textareaValue
    .split("\n")
    .map((id) => id.trim())
    .filter(Boolean);

  /* ---------------- HELPERS ---------------- */
  const isSpare = () => !!watch("spare_parts");
  const isRecycled = () => !!watch("recycled_date");

  /* ---------------- COLOR HANDLERS ---------------- */
  const setWhite = () => {
    if (isSpare()) return; // 🚫 BLOCK spare → color
    setValue("blue", 0);
    setValue("green", 0);
    setValue("recycled_date", null);
  };

  const setBlue = () => {
    if (isSpare()) return; // 🚫 BLOCK spare → color
    setValue("blue", 1);
    setValue("green", 0);
    setValue("recycled_date", null);
  };

  const setGreen = () => {
    if (isSpare()) return; // 🚫 BLOCK spare → color
    setValue("blue", 0);
    setValue("green", 1);
    setValue("recycled_date", null);
  };

  /* ---------------- RECYCLED / SPARE ---------------- */
  const setRecycled = () => {
    setValue("blue", 0);
    setValue("green", 0);
    setValue("spare_parts", null);
    setValue("recycled_date", new Date().toISOString().split("T")[0]);
  };

  const setSpare = () => {
    setValue("blue", 0);
    setValue("green", 0);
    setValue("recycled_date", null);
    setValue("spare_parts", new Date().toISOString().split("T")[0]);
  };

  /* ---------------- ACTIVE STATE ---------------- */
  const isWhite = () =>
    watch("blue") === 0 &&
    watch("green") === 0 &&
    !watch("recycled_date") &&
    !watch("spare_parts");

  const isBlue = () => watch("blue") === 1;
  const isGreen = () => watch("green") === 1;

  /* ---------------- SUBMIT ---------------- */
  const onSubmit = () => {
    // spare → recycled only
    if (watch("recycled_date")) {
      moveDevicesMutation.mutate({
        recycled_date: watch("recycled_date"),
      });
      return;
    }

    if (watch("spare_parts")) {
      moveDevicesMutation.mutate({
        spare_parts: watch("spare_parts"),
      });
      return;
    }

    // colors
    moveDevicesMutation.mutate({
      blue: watch("blue"),
      green: watch("green"),
    });
  };

  /* ---------------- MUTATION ---------------- */
  const moveDevicesMutation = useMutation({
    mutationFn: async (deviceOptions) => {
      return axiosInstance.post(
        window.REACT_APP_API_ENDPOINT + "/device-options/multiple-update",
        {
          device_unique_ids: uniqueIdsArray,
          device_options: deviceOptions,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries();
      toast.success("Devices moved successfully");
      closeDialog();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Error");
    },
  });

  /* ---------------- UI ---------------- */
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {["blue", "green", "recycled_date", "spare_parts"].map((f) => (
        <input key={f} type="hidden" {...register(f)} />
      ))}

      <textarea
        className="w-full border rounded-md p-2 min-h-[100px]"
        value={textareaValue}
        onChange={(e) => setTextareaValue(e.target.value)}
        placeholder="One ID per line"
      />

      <div className="flex gap-2 my-4">
        <button
          type="button"
          onClick={setWhite}
          disabled={isSpare()}
          className={`px-3 py-1 rounded ${
            isWhite()
              ? "bg-neutral-500 text-white"
              : "border"
          } ${isSpare() ? "opacity-40 cursor-not-allowed" : ""}`}
        >
          White
        </button>

        <button
          type="button"
          onClick={setBlue}
          disabled={isSpare()}
          className={`px-3 py-1 rounded ${
            isBlue()
              ? "bg-blue-500 text-white"
              : "border"
          } ${isSpare() ? "opacity-40 cursor-not-allowed" : ""}`}
        >
          Blue
        </button>

        <button
          type="button"
          onClick={setGreen}
          disabled={isSpare()}
          className={`px-3 py-1 rounded ${
            isGreen()
              ? "bg-green-500 text-white"
              : "border"
          } ${isSpare() ? "opacity-40 cursor-not-allowed" : ""}`}
        >
          Green
        </button>

        <button
          type="button"
          onClick={setSpare}
          className={`px-3 py-1 rounded ${
            watch("spare_parts")
              ? "bg-yellow-500 text-white"
              : "border"
          }`}
        >
          Spare Parts
        </button>

        <button
          type="button"
          onClick={setRecycled}
          className={`px-3 py-1 rounded ${
            isRecycled()
              ? "bg-indigo-500 text-white"
              : "border"
          }`}
        >
          Recycled
        </button>
      </div>

      <button
        type="submit"
        disabled={!uniqueIdsArray.length || moveDevicesMutation.isLoading}
        className="bg-blue-600 text-white px-4 py-2 rounded"
      >
        {moveDevicesMutation.isLoading ? "Moving..." : "Move Devices"}
      </button>
    </form>
  );
}
